/**
 * Which devices an account actually owns, said by the account and not by the
 * server.
 *
 * Everything else rests on this. A bundle is pinned per device, so a server
 * that ADDS a device is not caught by pinning — it is a device nobody has an
 * opinion about yet. Publishing one needs nothing but an ordinary account
 * token, which root, the JWT secret, or the database all provide. Removing one
 * needs even less. Key transparency makes both visible after the fact; only a
 * signature makes them impossible.
 *
 * So an account has a MASTER identity, and the master signs a versioned list
 * of the devices it authorises. The server stores the blob and hands it back;
 * it cannot produce one, and the only lie left to it is an old list, which the
 * version and the chain below catch.
 *
 * Two deliberate differences from Signal, both because of what we are:
 *
 *   Signal shares ONE identity key across an account's devices, handed to each
 *   new device during linking, so a device the server invents cannot sign
 *   anything. That makes the safety number per account — which is why linking
 *   a laptop does not alarm the people you talk to. We keep per-device keys
 *   (a device compromise stays a device compromise) and get the same property
 *   a different way: peers pin the MASTER key, and the device list moves
 *   underneath it.
 *
 *   The master secret therefore lives in one place, not on every device. That
 *   is a real cost — the device holding it must take part in adding another —
 *   and it is why it belongs in the passphrase-protected history archive,
 *   which is already the road a person takes to a new phone.
 */

import nacl from 'tweetnacl';

const LIST_CONTEXT = 'VoxioxDeviceListV1';
export const GENESIS_PREV = 'voxiox-device-list-genesis';

const enc = new TextEncoder();
const b64 = (u8) => btoa(String.fromCharCode(...u8));
function unb64(s) {
  const bin = atob(String(s).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * The exact bytes that get signed.
 *
 * Written by hand rather than JSON.stringify because a signature over a
 * serialisation nobody pinned down is a signature over whatever the next
 * library version decides. Field order, separators and the absence of
 * whitespace are part of the format.
 */
export function canonicalList(list) {
  const parts = [
    LIST_CONTEXT,
    String(list.account),
    String(list.version),
    String(list.prev || GENESIS_PREV),
    String(list.issuedAt || 0),
  ];
  for (const d of list.devices) {
    // Label is NOT signed: it is a display string a person may rename, and
    // re-signing the whole list to fix a typo would need the master key.
    parts.push([d.deviceId, d.identitySig, d.identityDh].join(','));
  }
  return enc.encode(parts.join('\x1e'));
}

/** A short, stable hash of a list, so the next one can name it. */
export function listHash(list) {
  const h = nacl.hash(canonicalList(list)).slice(0, 24);
  return b64(h).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Sign a list with the account master secret.
 *
 * Refuses an entry with no identity keys. That is not defensive
 * programming — it is the exact bug that shipped: the device endpoint returned
 * ids and labels only, the keys came through as undefined, and the result was
 * a list that verified beautifully and authorised nobody, because every entry
 * matched nothing. Signing nonsense is worse than failing, since the
 * signature makes the nonsense authoritative.
 */
export function signList(masterSecretKey, list) {
  for (const d of list.devices) {
    if (!d.deviceId || !d.identitySig || !d.identityDh) {
      throw new Error(`Устройство ${d.deviceId || '?'} без ключей — такой список подписывать нельзя`);
    }
  }
  return {
    list,
    sig: b64(nacl.sign.detached(canonicalList(list), masterSecretKey)),
  };
}

export const LIST_ERROR = {
  NO_MASTER: 'no-master',           // we hold no pinned master for this account
  BAD_SIGNATURE: 'bad-signature',   // not signed by the master we pinned
  ROLLBACK: 'rollback',             // an older list than one we already saw
  FORKED: 'forked',                 // does not continue from the list we saw
  GAP: 'gap',                       // newer by more than one step, and no chain to walk
  MALFORMED: 'malformed',
};

/**
 * Check a signed list against what we already know about this account.
 *
 * `seen` is `{ version, hash }` from the last list we accepted, or null on
 * first contact. Rollback and fork are separate answers on purpose: an old
 * list is a server withholding an update, a forked one is a server that has
 * two histories, and the second is much worse news.
 */
export function verifyList(signed, masterKeyB64, seen = null) {
  if (!signed || !signed.list || !signed.sig) return { ok: false, error: LIST_ERROR.MALFORMED };
  const { list } = signed;
  if (!Array.isArray(list.devices) || !Number.isInteger(list.version) || list.version < 1) {
    return { ok: false, error: LIST_ERROR.MALFORMED };
  }
  if (!masterKeyB64) return { ok: false, error: LIST_ERROR.NO_MASTER };

  let good = false;
  try {
    good = nacl.sign.detached.verify(canonicalList(list), unb64(signed.sig), unb64(masterKeyB64));
  } catch (e) { return { ok: false, error: LIST_ERROR.MALFORMED }; }
  if (!good) return { ok: false, error: LIST_ERROR.BAD_SIGNATURE };

  if (seen) {
    if (list.version < seen.version) return { ok: false, error: LIST_ERROR.ROLLBACK, seen };
    // Same version must be the same list. A second list at the same number is
    // the clearest possible statement that the server is keeping two.
    if (list.version === seen.version && listHash(list) !== seen.hash) {
      return { ok: false, error: LIST_ERROR.FORKED, seen };
    }
    // A newer list must name its predecessor. Without this the server could
    // jump straight to a version far ahead, built on a history nobody saw.
    if (list.version === seen.version + 1 && list.prev !== seen.hash) {
      return { ok: false, error: LIST_ERROR.FORKED, seen };
    }
    // Two or more steps ahead: `prev` names a list we never saw, so the
    // signature proves the master issued THIS list and nothing about how it
    // relates to ours. Only walking every step (verifyChain) makes a jump a
    // history rather than a claim (audit 01.09 P1-01).
    if (list.version > seen.version + 1) {
      return { ok: false, error: LIST_ERROR.GAP, seen };
    }
  }

  return { ok: true, version: list.version, hash: listHash(list), devices: list.devices };
}

/**
 * Walk a chain of signed lists from the one we hold to the newest.
 *
 * `chain` is every signed version after `seen`, ascending, as the server
 * returns it for `?since=`. Each step is verified against the one before it
 * with the ordinary rules — signature, next version, names its predecessor —
 * so a server that skipped, reordered or rewrote a step is caught at that
 * step. Returns the verdict for the last list, or the first failure.
 */
export function verifyChain(chain, masterKeyB64, seen = null) {
  if (!Array.isArray(chain) || chain.length === 0) {
    return { ok: false, error: seen ? LIST_ERROR.GAP : LIST_ERROR.MALFORMED, seen };
  }
  let cursor = seen;
  let last = null;
  for (const step of chain) {
    if (!step || !step.list) return { ok: false, error: LIST_ERROR.MALFORMED, seen };
    // The server may echo the version we hold; that is not a step.
    if (cursor && step.list.version === cursor.version) {
      if (listHash(step.list) !== cursor.hash) return { ok: false, error: LIST_ERROR.FORKED, seen: cursor };
      continue;
    }
    const v = verifyList({ list: step.list, sig: step.sig }, masterKeyB64, cursor);
    if (!v.ok) return v;
    cursor = { version: v.version, hash: v.hash };
    last = { ...v, list: step.list, sig: step.sig };
  }
  return last || { ok: false, error: LIST_ERROR.GAP, seen };
}

/** Is this device authorised by the list we verified? */
export function listAuthorises(list, deviceId, identitySig) {
  const d = list.devices.find(x => x.deviceId === deviceId);
  if (!d) return false;
  return !identitySig || d.identitySig === identitySig;
}

/**
 * The next version of a list, with a device added or removed.
 *
 * Returns the unsigned list; signing needs the master secret, which is the
 * whole point — this is the step the server cannot take.
 */
export function nextList(previous, { add = null, devices = null, removeDeviceId = null, account, issuedAt }) {
  const base = previous
    ? { account: previous.account, version: previous.version + 1, prev: listHash(previous),
        devices: previous.devices.slice() }
    : { account: String(account), version: 1, prev: GENESIS_PREV, devices: [] };
  // A whole set at once. Building a first list by adding one device at a time
  // gave it a version equal to the number of devices, and a `prev` pointing at
  // a list that never existed anywhere — caught by a test, which is the only
  // reason it is not in production.
  if (devices) {
    base.devices = devices.map(d => ({ deviceId: d.deviceId, identitySig: d.identitySig,
                                       identityDh: d.identityDh, label: d.label || null }));
  }
  if (removeDeviceId) base.devices = base.devices.filter(d => d.deviceId !== removeDeviceId);
  if (add) {
    base.devices = base.devices.filter(d => d.deviceId !== add.deviceId);
    base.devices.push({ deviceId: add.deviceId, identitySig: add.identitySig,
                        identityDh: add.identityDh, label: add.label || null });
  }
  // Sorted, so two devices building the same list from the same facts produce
  // the same bytes and therefore the same hash.
  base.devices.sort((a, b) => (a.deviceId < b.deviceId ? -1 : a.deviceId > b.deviceId ? 1 : 0));
  base.issuedAt = issuedAt || 0;
  return base;
}
